/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { html } from 'lit';
import { createId, pxToRem } from '@cds/core/internal';
export const formLayouts = ['vertical', 'vertical-inline', 'horizontal', 'horizontal-inline', 'compact'];
export const defaultFormLayout = 'horizontal';
export const defaultControlWidth = 'stretch';
export function associateInputAndLabel(input, label) {
    if (input && label) {
        assignInputId(input);
        label.setAttribute('for', input.id);
    }
}
export function associateInputToDatalist(input, datalist) {
    if (datalist) {
        assignInputId(input);
        datalist.id = `${input.id}-datalist`;
        input.setAttribute('list', datalist.id);
    }
}
export function assignInputId(input) {
    if (!input.id.length) {
        input.id = createId();
    }
}
export const layoutGroupToControlMapper = {
    'horizontal-inline': 'horizontal',
    'vertical-inline': 'vertical',
    horizontal: 'horizontal',
    vertical: 'vertical',
    compact: 'compact',
};
export function getStatusIcon(status) {
    return html `${status !== 'neutral'
        ? html ` <cds-button-action readonly class="status" cds-layout="align:shrink">
        <cds-icon
          status="${status === 'error' ? 'danger' : 'success'}"
          shape="${status === 'error' ? 'exclamation-circle' : 'check-circle'}"
          size="16"
          inner-offset=${4}
        ></cds-icon>
      </cds-button-action>`
        : ''} `;
}
export async function getLargestPrimaryLabelWidth(controls) {
    return Promise.all(controls.map(c => c.updateComplete)).then(() => {
        const primaryLabels = controls.filter(c => c.controlLabel?.action === 'primary');
        return pxToRem(Math.max(...primaryLabels.map(c => c.controlLabel.getBoundingClientRect().width)));
    });
}
export function controlIsWrapped(input, label, layout) {
    return (layout !== 'vertical' &&
        layout !== 'vertical-inline' &&
        input.getBoundingClientRect().top > label?.getBoundingClientRect().top + 12 // 12px buffer for rounding and inputs that are thin like the range input
    );
}
export function inlineControlListIsWrapped(inlineControls, layout) {
    const first = inlineControls[0];
    const last = inlineControls[inlineControls.length - 1];
    const isInlineLayout = layout === 'vertical-inline' || layout === 'horizontal-inline';
    return isInlineLayout && last.getBoundingClientRect().top > first.getBoundingClientRect().top;
}
export function isVerticalLayout(layout) {
    return layout === 'vertical' || layout === 'vertical-inline';
}
/**
 * Returns updated message status unless hidden then returns 'neutral'
 */
export async function getCurrentMessageStatus(messages) {
    const message = messages.find(m => m.status !== 'neutral');
    await message?.updateComplete;
    return message && !message.hidden ? message.status : 'neutral';
}
